import { useMutation, useQueryClient } from 'react-query';
import {
  allXml,
  designatedNotes,
  auxiliaryDocumentsDownloadAllPDF,
  generatePdfCorrection,
  manifestNotes,
  numberInutilization,
  numberInutilizationXml,
  transmissionCorrection,
} from './AuxiliaryDocuments.services';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { RecipientInvoiceQueryFilter } from '../../invoice/recipientInvoice/recipientInvoice.types';
import { NUMBER_INUTILIZATION_LIST_KEY } from '../../invoice/numberInutilization/numberInutilization.hooks';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { AxiosError } from 'axios';
import arrayBufferParse from '@/utils/functions/arrayBufferParse';

export function useInvoiceAllXml() {
  return useMutation(allXml, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'XMLs da empresa baixados com sucesso!',
      });
    },

    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        const dataView = new DataView(err.response?.data);
        const decoder = new TextDecoder('utf8');
        const response = JSON.parse(decoder.decode(dataView));

        notificationError(response);
      }
    },
  });
}

export function useTransmissionCorrection() {
  const queryClient = useQueryClient();

  return useMutation(transmissionCorrection, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'Carta de correção transmitida',
      });

      queryClient.invalidateQueries(['corrections']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useGeneratePdfCorrection() {
  return useMutation(generatePdfCorrection, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'PDF da carta de correção baixado',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useDesignatedNotes() {
  const queryClient = useQueryClient();

  return useMutation(designatedNotes, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        'recipient-invoice-list',
        { companyId: data.companyId } as RecipientInvoiceQueryFilter,
      ]);

      notification.success({
        message: 'Sucesso',
        description: 'Pesquisa de notas feitas com sucesso',
      });
    },

    onError(error: AxiosError) {
      if (
        error.isAxiosError &&
        error.response?.data.message?.includes('Retorno SEFAZ')
      ) {
        notification.info({ message: error.response.data.message });
      } else {
        notificationError(error);
      }
    },
  });
}

export function useManifestNotes() {
  const queryClient = useQueryClient();

  return useMutation(manifestNotes, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        'recipient-invoice-list',
        { companyId: data.companyId } as RecipientInvoiceQueryFilter,
      ]);

      notification.success({
        message: 'Sucesso',
        description: 'Notas manifestadas com sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceNumberInutilization() {
  const queryChanel = useQueryChanel();

  return useMutation(numberInutilization, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'Numerações inutilizadas com sucesso!',
      });

      queryChanel({ invalidateQueries: [[NUMBER_INUTILIZATION_LIST_KEY]] });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceNumberInutilizationXml() {
  return useMutation(numberInutilizationXml, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'XML baixado com sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceAuxiliaryDocumentsAllPDF() {
  return useMutation({
    mutationFn: auxiliaryDocumentsDownloadAllPDF,
    onSuccess() {
      notification.success({
        message: 'Arquivo zip dos PDFs baixado com sucesso!',
      });
    },
    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        interface ResponseError {
          code: string;
          message: string;
          timestamp: string;
        }

        const response = arrayBufferParse<ResponseError>(err.response.data);

        return notification.error({
          message: 'Download dos PDFs',
          description: response?.message,
        });
      }

      notificationError(error);
    },
  });
}
